import { type DocumentReference } from 'firebase/firestore';

import { DocumentCollectionType } from 'types/DocumentCollectionType';
import { DocumentType } from 'types/DocumentType';
import { type LogEntry } from 'types/LogEntry';
import { nullTimestamp } from 'utils/FirestoreUtils';
import { encodeURIPathname } from 'utils/String';

import type Bundle from './Bundle';
import { DocumentSchema } from './DocumentSchema';
import DocumentStore from './DocumentStore';
import { type HtmlAdInstance } from './HtmlAdInstance';

export class BundleElementSchema extends DocumentSchema {
    adId = '';
    archivesCreated = true;
    buildLog: LogEntry[] = [];
    builds: HtmlAdInstance[] = [];
    createTime = nullTimestamp();
    creative = '';
    dimensions: [number, number] = [NaN, NaN];
    errors: string[] = [];
    label = '';
    losslessScreenshotPath = '';
    lossyScreenshotActualSize = NaN;
    lossyScreenshotMaxSize = NaN;
    lossyScreenshotPath = '';
    modifiedTime = nullTimestamp();
    mutex = 0;
    screenshotScale = 1;
    stagePrefix = '';
    status:
        | 'notset'
        | 'pending'
        | 'new'
        | 'changing'
        | 'complete'
        | 'error' = 'notset';
}

/**
 * A BundleElement lives in its Bundle's `elements` collection, and needs only
 * the owning bundle's baseURL from it. Structural, so this file needs no
 * runtime import of Bundle (which imports BundleElement for that collection).
 */
export interface BundleElementParent {
    parent: { baseURL: string };
}

export default class BundleElement extends DocumentStore<BundleElementSchema> {
    declare parent: BundleElementParent | undefined;

    type = DocumentType.BUNDLE_ELEMENT;
    collectionType = DocumentCollectionType.BUNDLE_ELEMENT;
    stagingURL = '';
    archiveURL = '';
    staticBackupURL = '';
    losslessBackupURL = '';

    schema = new BundleElementSchema();

    constructor(parent: BundleElementParent, docRef?: DocumentReference) {
        super(parent, docRef);
        this.becomeObservable();
    }

    onReady = () => {
        const bundle = this.parent?.parent;
        if (!bundle) return;
        if(!this.data.builds) return;
        const build = this.data.builds.find(
            (build) => build.buildType === 'dist'
        );
        if(!build) return;
        const indexFile = build.manifest.find(
            (file) => file.path === 'index.html'
        );
        if(!indexFile) return;

        const resolveBundleURL = (storagePath: string) => {
            const u = new URL(encodeURIPathname(storagePath), bundle.baseURL);
            return u.href;
        };

        this.stagingURL = resolveBundleURL(indexFile.storage);
        this.archiveURL = resolveBundleURL(build.archiveStorage);
        this.staticBackupURL = resolveBundleURL(this.data.lossyScreenshotPath);
        this.losslessBackupURL = resolveBundleURL(this.data.losslessScreenshotPath);
    }
}
